import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export const useScrollAnimation = (options: UseScrollAnimationOptions = {}) => {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

// Wrapper component for easy scroll animations
interface ScrollAnimationProps {
  children: React.ReactNode;
  className?: string;
  animation?: 'fade-up' | 'fade-in' | 'fade-left' | 'fade-right' | 'scale';
  delay?: number;
  duration?: number;
}

export const ScrollAnimation = ({
  children,
  className = '',
  animation = 'fade-up',
  delay = 0,
  duration = 450,
}: ScrollAnimationProps) => {
  const { ref, isVisible } = useScrollAnimation();

  const baseStyles = {
    transition: `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`,
    transitionDelay: `${delay}ms`,
  };

  const animations = {
    'fade-up': {
      initial: { opacity: 0, transform: 'translateY(12px)' },
      visible: { opacity: 1, transform: 'translateY(0)' },
    },
    'fade-in': {
      initial: { opacity: 0 },
      visible: { opacity: 1 },
    },
    'fade-left': {
      initial: { opacity: 0, transform: 'translateX(-12px)' },
      visible: { opacity: 1, transform: 'translateX(0)' },
    },
    'fade-right': {
      initial: { opacity: 0, transform: 'translateX(12px)' },
      visible: { opacity: 1, transform: 'translateX(0)' },
    },
    scale: {
      initial: { opacity: 0, transform: 'scale(0.98)' },
      visible: { opacity: 1, transform: 'scale(1)' },
    },
  };

  const currentAnimation = animations[animation];
  const animationStyles = isVisible ? currentAnimation.visible : currentAnimation.initial;

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...baseStyles, ...animationStyles }}
    >
      {children}
    </div>
  );
};
